import { useQuery } from "react-query";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { CsrIndicatorUnitCodeSchema } from "./unitCodeSchema";
import { LeverValueColor, LeverValueColorSchema } from "src/utils/csrStrategyValueColor";
import i18next from "i18next";
import { useApplicationParameters } from "../appParams/useApplicationParameters";

export const getAllStrategyKey = "getAllStrategyKey" as const;

export const useGetAllStrategy = () => {
    const { isFeatureActivated, isLoading: parametersLoading } = useApplicationParameters();
    const strategyEnabled = !parametersLoading && isFeatureActivated("FS-strategy");
    const query = useQuery({
        enabled: strategyEnabled,
        queryKey: [getAllStrategyKey, i18next.resolvedLanguage],
        queryFn: async () => {
            const url = `/api/steering/strategies`;

            const params = {
                page: 1,
                itemsPerPage: 1,
            };
            return await loggedInApiCall.get(url, {
                params,
            });
        },
        select: (request) => {
            const data = request.data["hydra:member"];
            const zodParsedData = zodParseWithDetailedLog(data, CsrStrategySchema.array(), {
                operation: "GET",
                endpoint: "/steering/strategies",
            });
            return zodParsedData;
        },
    });

    return {
        allStrategy: strategyEnabled ? query.data : undefined,
        isReady: !parametersLoading && (!strategyEnabled || query.isFetched),
        ...query,
    };
};

export type CsrIndicator = {
    "@id": IRI;
    id?: string;
    position?: number | null;
    name: string;
    leverShortName?: string | null;
    leverFullName?: string | null;
    indicatorLabel?: string | null;
    valueColor?: LeverValueColor | null;
    richContent?: string | null;
    unitCode: string;
    currentValue: string;
    shortTermTarget: string;
    longTermTarget: string;
};

const CsrIndicatorSchema: z.ZodType<CsrIndicator> = z.object({
    "@id": z.string(),
    id: z.string().optional(),
    position: z.number().int().nonnegative().nullable().optional(),
    name: z.string(),
    leverShortName: z.string().nullable().optional(),
    leverFullName: z.string().nullable().optional(),
    indicatorLabel: z.string().nullable().optional(),
    valueColor: LeverValueColorSchema.nullable().optional(),
    richContent: z.string().nullable().optional(),
    unitCode: CsrIndicatorUnitCodeSchema,
    currentValue: z.string(),
    shortTermTarget: z.string(),
    longTermTarget: z.string(),
});

export type CsrPillar = {
    "@id": string;
    id: string;
    position?: number | null;
    name: string;
    themes?: number[];
    icon: string;
    indicators: CsrIndicator[];
};

const CsrPillarSchema: z.ZodType<CsrPillar> = z.object({
    "@id": z.string(),
    id: z.string(),
    position: z.number().int().nonnegative().nullable().optional(),
    name: z.string(),
    themes: z.number().array().optional(),
    icon: z.string(),
    indicators: CsrIndicatorSchema.array(),
});

export type CsrStrategy = {
    "@id": IRI;
    id: string;
    name: string;
    description?: string;
    campaign: IRI;
    endCampaign?: IRI | null;
    pillars: CsrPillar[];
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
};

const CsrStrategySchema: z.ZodType<CsrStrategy> = z.object({
    "@id": z.string(),
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    campaign: z.string(),
    endCampaign: z.string().nullable().optional(),
    pillars: CsrPillarSchema.array(),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
});
